import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import type { LucideIcon } from 'lucide-react'
import {
  BadgeDollarSign,
  ChevronRight,
  Clock,
  FolderKanban,
  HelpCircle,
  LayoutDashboard,
  MessageSquare,
  Scissors,
  Settings,
  Sparkles,
  Users,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { useCustomerProjects } from '@/hooks/projects'
import { useOnboardingSubmissions } from '@/hooks/onboarding'
import { useConversations, useUnreadCount } from '@/hooks/messaging'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui'
import { Skeleton } from '@/components/ui/skeleton'
import { UserAvatar } from '@/components/UserAvatar'
import type { Conversation, Message, ProjectDetail } from '@/lib/api'
import { cn } from '@/lib/utils'

type DashboardTab = 'projects' | 'proposals' | 'messages'

type SidebarLink = {
  id: DashboardTab | 'settings' | 'support'
  label: string
  description: string
  icon: LucideIcon
  disabled?: boolean
}

const sidebarLinks: SidebarLink[] = [
  {
    id: 'projects',
    label: 'Active projects',
    description: 'Track progress and collaborators',
    icon: LayoutDashboard,
  },
  {
    id: 'proposals',
    label: 'Proposals',
    description: 'Review developer offers',
    icon: FolderKanban,
  },
  {
    id: 'messages',
    label: 'Messages',
    description: 'Keep conversations moving',
    icon: MessageSquare,
  },
  {
    id: 'settings',
    label: 'Account settings',
    description: 'Payment methods & billing',
    icon: Settings,
    disabled: true,
  },
  {
    id: 'support',
    label: 'Help & support',
    description: 'Need a hand?',
    icon: HelpCircle,
    disabled: true,
  },
]

function formatDate(input: string) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(input))
  } catch (error) {
    return input
  }
}

function getLatestMessage(
  project: ProjectDetail,
  conversationsByProposal: Map<number, Conversation>
): Message | null {
  const candidates = project.proposals
    .map((proposal) => conversationsByProposal.get(proposal.id)?.last_message)
    .filter(Boolean) as Message[]

  if (!candidates.length) {
    return null
  }

  return candidates.sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )[0]
}

function getUnreadCountForProject(
  project: ProjectDetail,
  conversationsByProposal: Map<number, Conversation>
) {
  return project.proposals.reduce((total, proposal) => {
    const conversation = conversationsByProposal.get(proposal.id)
    return total + (conversation?.unread_count ?? 0)
  }, 0)
}

export default function Dashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const prefersReducedMotion = useReducedMotion()
  const unreadCount = useUnreadCount()
  const [activeTab, setActiveTab] = useState<DashboardTab>('projects')
  const [selectedProposalProjectId, setSelectedProposalProjectId] = useState<number | null>(null)

  const customerProjects = useCustomerProjects()
  const { data: onboardingSubmissions } = useOnboardingSubmissions()
  const {
    data: conversations,
    isLoading: conversationsLoading,
    error: conversationsError,
  } = useConversations()

  const projects = customerProjects.data ?? []
  const activeProjects = useMemo(
    () => projects.filter((project) => ['open', 'in_progress'].includes(project.status)),
    [projects]
  )
  const projectsWithProposals = useMemo(
    () => projects.filter((project) => project.proposals.length > 0),
    [projects]
  )

  useEffect(() => {
    if (!selectedProposalProjectId && projectsWithProposals.length > 0) {
      setSelectedProposalProjectId(projectsWithProposals[0].id)
    }
  }, [projectsWithProposals, selectedProposalProjectId])

  const selectedProposalProject =
    projectsWithProposals.find((project) => project.id === selectedProposalProjectId) ?? null

  const conversationsByProposal = useMemo(() => {
    const map = new Map<number, Conversation>()
    ;(conversations ?? []).forEach((conversation) => {
      if (conversation.proposal_id) {
        map.set(conversation.proposal_id, conversation)
      }
    })
    return map
  }, [conversations])

  const dashboardStats = useMemo(() => {
    const openInvites = projects.reduce((total, project) => total + project.invitations.length, 0)
    const totalOffers = projects.reduce((total, project) => total + project.proposals.length, 0)

    return [
      { label: 'Active projects', value: activeProjects.length },
      { label: 'Developer offers', value: totalOffers },
      { label: 'Invitations sent', value: openInvites },
    ]
  }, [projects, activeProjects.length])

  const renderActiveProjects = () => {
    if (customerProjects.isLoading) {
      return (
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 2 }).map((_, index) => (
            <Skeleton key={index} className="h-60 rounded-2xl" />
          ))}
        </div>
      )
    }

    if (customerProjects.error) {
      return (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardHeader>
            <CardTitle className="text-destructive">We couldn’t load your projects</CardTitle>
            <CardDescription className="text-destructive">
              Please refresh the page or try again later.
            </CardDescription>
          </CardHeader>
        </Card>
      )
    }

    if (!activeProjects.length) {
      return (
        <Card className="border-dashed shadow-warm-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Post your first project
            </CardTitle>
            <CardDescription>
              Start a proposal to bring developers into your workspace.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/post">Create project</Link>
            </Button>
          </CardContent>
        </Card>
      )
    }

    return (
      <div className="grid gap-4">
        {activeProjects.map((project) => {
          const acceptedDevelopers = project.proposals.filter((proposal) => proposal.accepted)
          const latestMessage = getLatestMessage(project, conversationsByProposal)
          const latestMessageAvatar = latestMessage
            ? ((latestMessage as { sender_avatar_url?: string | null }).sender_avatar_url ??
              (latestMessage.sender_id === user?.id
                ? (user?.profile?.avatar_url ?? null)
                : undefined))
            : undefined
          const unreadForProject = getUnreadCountForProject(project, conversationsByProposal)

          return (
            <Card
              key={project.id}
              className="border-border/70 bg-card/90 shadow-warm-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-warm-md"
            >
              <CardHeader className="space-y-2">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-xl font-semibold">{project.title}</CardTitle>
                    <CardDescription>
                      Posted {formatDate(project.created_at)} • {project.account_name}
                    </CardDescription>
                  </div>
                  <div className="flex flex-col items-end gap-2 text-xs">
                    <Badge variant="outline" className="uppercase tracking-wide">
                      {project.status.replace(/_/g, ' ')}
                    </Badge>
                    <Badge variant="secondary" className="capitalize">
                      {project.project_type}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <p className="text-sm font-semibold text-muted-foreground">
                    Connected developers
                  </p>
                  {acceptedDevelopers.length ? (
                    <div className="mt-3 space-y-2">
                      {acceptedDevelopers.map((proposal) => (
                        <div
                          key={proposal.id}
                          className="flex items-center gap-3 rounded-lg border border-border/50 bg-muted/40 px-3 py-2"
                        >
                          <UserAvatar
                            size="sm"
                            avatarUrl={proposal.submitted_by_avatar_url}
                            username={proposal.submitted_by_username}
                          />
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">
                              {proposal.submitted_by_username}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              Accepted developer
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-1 text-sm text-muted-foreground">
                      No developers have been accepted yet.
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-primary" />
                    {unreadForProject > 0
                      ? `${unreadForProject} unread message${unreadForProject === 1 ? '' : 's'}`
                      : 'No new messages'}
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    {project.proposals.length} offer
                    {project.proposals.length === 1 ? '' : 's'}
                  </div>
                </div>

                {latestMessage && (
                  <div className="rounded-lg border bg-muted/60 p-4 text-sm">
                    <div className="flex items-start gap-3">
                      <UserAvatar
                        size="sm"
                        avatarUrl={latestMessageAvatar}
                        username={latestMessage.sender_username}
                      />
                      <div className="flex-1">
                        <p className="flex items-center gap-2 font-medium">
                          {latestMessage.sender_username}
                          <span className="text-xs text-muted-foreground">
                            {formatDate(latestMessage.created_at)}
                          </span>
                        </p>
                        <p className="mt-1 line-clamp-2 text-muted-foreground">
                          {latestMessage.content}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  <Button asChild size="sm">
                    <Link to={`/projects/${project.id}`}>Open project</Link>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => navigate('/messages')}
                  >
                    <MessageSquare className="h-4 w-4" />
                    Go to messages
                  </Button>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    )
  }

  const renderProposalSection = () => {
    if (!projectsWithProposals.length) {
      return (
        <Card className="border-dashed border-primary/40 bg-primary/5 shadow-warm-sm">
          <CardHeader>
            <CardTitle>Invite developers to your proposals</CardTitle>
            <CardDescription>
              Post a project and you’ll start seeing developer offers here.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline">
              <Link to="/post">Create proposal</Link>
            </Button>
          </CardContent>
        </Card>
      )
    }

    return (
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <CardTitle>Proposals & offers</CardTitle>
              <CardDescription>
                Select a proposal to preview the offers developers have shared.
              </CardDescription>
            </div>
            <Button variant="outline" onClick={() => navigate('/projects')}>
              Manage projects
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-5">
            <div className="space-y-2 md:col-span-2">
              {projectsWithProposals.map((project) => (
                <button
                  key={project.id}
                  type="button"
                  onClick={() => setSelectedProposalProjectId(project.id)}
                  className={cn(
                    'w-full rounded-lg border p-4 text-left transition-[colors,box-shadow] duration-300',
                    project.id === selectedProposalProjectId
                      ? 'border-primary bg-primary/5'
                      : 'hover:bg-muted/60 hover:shadow-warm-sm'
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="font-semibold leading-tight">{project.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {project.proposals.length} developer offer
                        {project.proposals.length === 1 ? '' : 's'}
                      </p>
                    </div>
                    <ChevronRight
                      className={cn(
                        'h-4 w-4 text-muted-foreground',
                        project.id === selectedProposalProjectId && 'text-primary'
                      )}
                    />
                  </div>
                </button>
              ))}
            </div>
            <div className="space-y-4 md:col-span-3">
              {selectedProposalProject ? (
                selectedProposalProject.proposals.length ? (
                  selectedProposalProject.proposals.map((proposal) => {
                    const conversation = conversationsByProposal.get(proposal.id)
                    return (
                      <Card key={proposal.id} className="border-dashed shadow-warm-sm">
                        <CardHeader className="space-y-1">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-3">
                              <UserAvatar
                                size="sm"
                                avatarUrl={proposal.submitted_by_avatar_url}
                                username={proposal.submitted_by_username}
                              />
                              <CardTitle className="text-lg">
                                {proposal.submitted_by_username}
                              </CardTitle>
                            </div>
                            {proposal.accepted ? (
                              <Badge>Accepted</Badge>
                            ) : (
                              <Badge variant="secondary">Pending</Badge>
                            )}
                          </div>
                          <CardDescription>
                            Received {formatDate(proposal.created_at)}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <p className="whitespace-pre-line text-sm text-muted-foreground">
                            {proposal.cover_letter}
                          </p>
                          <div className="flex flex-wrap gap-4 text-sm">
                            {proposal.hourly_rate && (
                              <span className="flex items-center gap-2">
                                <Clock className="h-4 w-4" />${proposal.hourly_rate}/hr
                              </span>
                            )}
                            {proposal.fixed_price && (
                              <span className="flex items-center gap-2">
                                <BadgeDollarSign className="h-4 w-4" />
                                Fixed ${proposal.fixed_price}
                              </span>
                            )}
                            {conversation && (
                              <span className="flex items-center gap-2">
                                <MessageSquare className="h-4 w-4" />
                                {conversation.unread_count > 0
                                  ? `${conversation.unread_count} unread`
                                  : 'No unread messages'}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {conversation && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/messages/${conversation.id}`)}
                              >
                                Open conversation
                              </Button>
                            )}
                            <Button size="sm" asChild>
                              <Link to={`/projects/${selectedProposalProject.id}`}>
                                View offers
                              </Link>
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )
                  })
                ) : (
                  <div className="rounded-lg border p-6 text-sm text-muted-foreground">
                    No offers for this proposal yet.
                  </div>
                )
              ) : (
                <div className="rounded-lg border p-6 text-sm text-muted-foreground">
                  Select a proposal to preview developer responses.
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  const renderMessagesPreview = () => {
    if (conversationsLoading) {
      return (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-20 rounded-lg" />
          ))}
        </div>
      )
    }

    if (conversationsError) {
      return (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardHeader>
            <CardTitle className="text-lg text-destructive">Couldn’t load your messages</CardTitle>
            <CardDescription className="text-destructive">
              Open the Messages tab to try again.
            </CardDescription>
          </CardHeader>
        </Card>
      )
    }

    if (!conversations?.length) {
      return (
        <Card className="border-dashed shadow-warm-sm">
          <CardHeader>
            <CardTitle>Start a conversation</CardTitle>
            <CardDescription>
              As proposals turn into matches, messages from developers will appear here.
            </CardDescription>
          </CardHeader>
        </Card>
      )
    }

    const recentConversations = conversations.slice(0, 4)

    return (
      <div className="space-y-3">
        {recentConversations.map((conversation) => {
          const senderAvatar = conversation.last_message
            ? ((conversation.last_message as { sender_avatar_url?: string | null })
                .sender_avatar_url ??
              (conversation.last_message.sender_id === user?.id
                ? (user?.profile?.avatar_url ?? null)
                : undefined))
            : undefined

          return (
            <Card
              key={conversation.id}
              className="border-border/70 shadow-warm-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-warm-md"
            >
              <CardHeader className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3">
                    <UserAvatar
                      size="sm"
                      avatarUrl={senderAvatar}
                      username={
                        conversation.last_message?.sender_username ??
                        `Proposal #${conversation.proposal_id}`
                      }
                    />
                    <div>
                      <CardTitle className="text-base">
                        Proposal #{conversation.proposal_id}
                      </CardTitle>
                      {conversation.last_message ? (
                        <CardDescription className="text-sm">
                          <span className="font-semibold">
                            {conversation.last_message.sender_username}:
                          </span>{' '}
                          {conversation.last_message.content}
                        </CardDescription>
                      ) : (
                        <CardDescription>No messages yet</CardDescription>
                      )}
                    </div>
                  </div>
                  {conversation.unread_count > 0 && (
                    <Badge variant="destructive">{conversation.unread_count} unread</Badge>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Updated {formatDate(conversation.updated_at)}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8"
                    onClick={() => navigate(`/messages/${conversation.id}`)}
                  >
                    Open thread
                  </Button>
                </div>
              </CardHeader>
            </Card>
          )
        })}
      </div>
    )
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
      <aside className="space-y-6 rounded-2xl border border-border/70 bg-card/80 p-6 shadow-warm-md">
        <div className="flex items-center gap-3">
          <UserAvatar
            size="lg"
            avatarUrl={user?.profile?.avatar_url ?? null}
            name={user?.username}
            className="border border-border/70"
          />
          <div>
            <p className="font-semibold">
              {user?.first_name ? `${user.first_name} ${user.last_name ?? ''}` : user?.username}
            </p>
            <p className="text-sm capitalize text-muted-foreground">
              {user?.profile?.role ?? 'customer'}
            </p>
          </div>
        </div>

        <div className="grid gap-3">
          {dashboardStats.map((stat, i) => (
            <motion.div
              key={stat.label}
              className="rounded-xl border bg-muted/30 p-3 text-sm shadow-warm-sm"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <p className="text-muted-foreground">{stat.label}</p>
              <p className="text-2xl font-semibold">{stat.value}</p>
            </motion.div>
          ))}
          <motion.div
            className="rounded-xl border bg-muted/30 p-3 text-sm shadow-warm-sm"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.3,
              delay: dashboardStats.length * 0.08,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
          >
            <p className="text-muted-foreground">Unread messages</p>
            <p className="text-2xl font-semibold">{unreadCount}</p>
          </motion.div>
        </div>

        <div className="space-y-2">
          {sidebarLinks.map((link) => {
            const Icon = link.icon
            const isActive = activeTab === link.id
            const isDisabled = link.disabled
            return (
              <button
                key={link.id}
                type="button"
                disabled={isDisabled}
                onClick={() => !isDisabled && setActiveTab(link.id as DashboardTab)}
                className={cn(
                  'w-full rounded-xl border px-4 py-3 text-left transition-[colors,box-shadow] duration-300',
                  isActive && 'border-primary bg-primary/5',
                  isDisabled && 'cursor-not-allowed opacity-60',
                  !isActive && !isDisabled && 'hover:bg-muted/50 hover:shadow-warm-sm'
                )}
              >
                <div className="flex items-center gap-3">
                  <Icon className="h-4 w-4" />
                  <div>
                    <p className="text-sm font-semibold">{link.label}</p>
                    <p className="text-xs text-muted-foreground">{link.description}</p>
                  </div>
                </div>
              </button>
            )
          })}
        </div>

        <div className="rounded-xl border bg-muted/20 p-4 text-sm">
          <p className="font-semibold">Need an extra hand?</p>
          <p className="text-muted-foreground">
            Invite teammates to collaborate on your proposals.
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-3 w-full"
            onClick={() => navigate('/developers')}
          >
            Browse developers
          </Button>
        </div>
      </aside>

      <section className="space-y-6">
        <motion.div
          className="relative overflow-hidden rounded-2xl border border-border/70 bg-gradient-to-br from-background via-background to-primary/5 p-6 shadow-warm-sm"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(91,146,121,0.06),transparent_70%)]" />
          <p className="relative text-sm text-muted-foreground">
            Welcome back{user?.first_name ? `, ${user.first_name}` : ''}
          </p>
          <h1 className="relative text-2xl font-bold tracking-tight">
            Let’s continue building your next project.
          </h1>
        </motion.div>

        {/* Onboarding CTA Card */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-background shadow-warm-sm transition-colors duration-300 hover:border-primary/50">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-[14px] bg-primary/[0.08]">
                  <Scissors className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Get Your Barbershop App</CardTitle>
                  <CardDescription>
                    Launch your own branded booking app in minutes. Configure barbers, services, and
                    branding -- we handle the rest.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {onboardingSubmissions && onboardingSubmissions.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Your submissions</p>
                  {onboardingSubmissions.map((sub) => (
                    <Link
                      key={sub.id}
                      to={`/onboarding/${sub.id}`}
                      className="flex items-center justify-between rounded-lg border px-4 py-2 transition-[colors,box-shadow] duration-300 hover:bg-muted/50 hover:shadow-warm-sm"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{sub.shop_name}</span>
                        <Badge variant="outline" className="text-xs capitalize">
                          {sub.status}
                        </Badge>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </Link>
                  ))}
                </div>
              )}
              <Button asChild>
                <Link to="/onboarding">
                  {onboardingSubmissions && onboardingSubmissions.length > 0
                    ? 'Continue Setup'
                    : 'Get Started'}
                </Link>
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        <AnimatePresence mode="wait">
          {activeTab === 'projects' && (
            <motion.div
              key="projects"
              className="space-y-6"
              initial={prefersReducedMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold">Active projects</h2>
                  <p className="text-sm text-muted-foreground">
                    View live proposals and recent developer activity.
                  </p>
                </div>
                <Button variant="outline" onClick={() => navigate('/projects')}>
                  Manage projects
                </Button>
              </div>
              {renderActiveProjects()}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Proposals overview</h3>
                {renderProposalSection()}
              </div>
            </motion.div>
          )}

          {activeTab === 'proposals' && (
            <motion.div
              key="proposals"
              initial={prefersReducedMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {renderProposalSection()}
            </motion.div>
          )}

          {activeTab === 'messages' && (
            <motion.div
              key="messages"
              className="space-y-4"
              initial={prefersReducedMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold">Conversations</h2>
                  <p className="text-sm text-muted-foreground">
                    Jump back into recent discussions with your developers.
                  </p>
                </div>
                <Button variant="outline" onClick={() => navigate('/messages')}>
                  View all messages
                </Button>
              </div>
              {renderMessagesPreview()}
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  )
}
